// @ts-check
import { gql, useQuery } from '@apollo/client';
import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Link,
  Table,
  TableBody,
  Text,
  useConfirmation,
} from '~/design-system';
import { useCommonLinks } from '../../../pages/imports/_config';
import Notification from '../../Notification';
import DefaultTableHeader from '../DefaultTableHeader';
import ImportsOverviewTableBodySkeleton from '../ImportsOverviewTableBodySkeleton';
import useDeleteImport from '../useDeleteImport';
import FailedImportRow from './FailedImportRow';

export default function ImportsFailed({ refetch }) {
  const {
    loading,
    failedImports,
    deleteImport,
    confirm,
    notification,
    setNotification,
  } = useImportsFailed({ refetch });

  const link = useCommonLinks();

  return (
    <React.Fragment>
      <Box sx={{ width: '100%', margin: '0 auto' }}>
        {!loading && failedImports.length === 0 ? (
          <Text sx={{ marginTop: 4 }}>
            You have no imports that have failed.{' '}
            <Link to={link.importsInProgress()}>Show imports in progress</Link>.
          </Text>
        ) : (
          <Table
            sx={{
              border: '1px solid',
              borderColor: 'brand.lightGray',
              width: '100%',
              textAlign: 'left',
            }}
          >
            <DefaultTableHeader />
            {loading ? (
              <ImportsOverviewTableBodySkeleton />
            ) : (
              <TableBody>
                {failedImports?.map((oneImport, i) => (
                  <FailedImportRow
                    key={i}
                    oneImport={oneImport}
                    onDelete={(oneImport) => {
                      confirm({
                        message: `Do you really want to remove ${oneImport.name}?`,
                        onConfirm: async () => {
                          const success = await deleteImport(
                            oneImport.importId
                          );
                          if (!success) {
                            setNotification({
                              message: 'There was an error, please try again!',
                            });
                          }
                        },
                      });
                    }}
                  />
                ))}
              </TableBody>
            )}
          </Table>
        )}
      </Box>

      {notification && (
        <Notification
          onClose={() => setNotification(undefined)}
          message={notification.message}
          // aLinkData={notification.aLinkData}
          variant={notification.success ? 'success' : 'error'}
        />
      )}
    </React.Fragment>
  );
}

export function useImportsFailed({ refetch: parentRefetch }) {
  const { workspaceId } = useParams();
  const { data, loading, refetch } = useQuery(query, {
    variables: { workspaceId },
  });
  const { deleteImport } = useDeleteImport({
    refetch: async () => {
      await parentRefetch();
      await refetch();
    },
  });
  const confirm = useConfirmation();
  /** @type {[import('../../Notification').Notification | undefined, React.Dispatch<React.SetStateAction<import('../../Notification').Notification | undefined>>]} */
  const [notification, setNotification] = React.useState();
  const failedImports = (
    data?.workspace?.dataImports?.nodes?.filter(Boolean) || []
  ).sort((a, b) => {
    return new Date(b?.createdAt) - new Date(a?.createdAt);
  });

  return {
    loading,
    failedImports,
    deleteImport,
    confirm,
    notification,
    setNotification,
  };
}

const query = gql`
  query ImportsFailedPage($workspaceId: ID!) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      dataImports(filter: { state: { eq: "ERROR" } }) {
        nodes {
          ...ImportOverview
          ...PastImports
          outputUrl
        }
      }
    }
  }

  fragment PastImports on DataImport {
    results {
      object
      addedCount
      updatedCount
      deletedCount
      unchangedCount
    }
  }

  fragment ImportOverview on DataImport {
    importId
    name
    creator {
      fullName
      email
    }
    importOptions
    source {
      ... on DataImportSourceTabular {
        url
      }
    }
    state {
      state
    }
    type
    createdAt
  }
`;
